import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { catchError, filter, Observable, throwError } from 'rxjs';
import { ErrorPageType } from 'src/app/helpers/enum/error-page-type.enum';
import { AuthInterceptorHelper } from 'src/app/services/auth-interceptor-helper/auth-interceptor-helper';

const AUTH_ERROR_URLS: string[] = [
  'api/v1/auth/jwt/token',
  'api/v1/me',
  'auth/jwt/loginWithSSO/',
  'auth/jwt/loginWithSSOWeb/',
  '/StandardSSO/GetLoginAuthMethod',
  '/StandardSSO/LoginDriverProcessPassword',
  'api/v1/auth/login/start',
  'api/v1/auth/login/password',
  'api/v1/auth/exchange'
];

const OFFLINE_MODE_SUPPORT_URLS: string[] = [
  '/dashboard',
  '/add-new-pre-use-inspection',
  '/pre-use-inspection',
  '/error',
  '/'
];

const AUTH_SKIP_UNAUTHORIZED_URLS: string[] = [
  'api/v1/auth/login/password'
];
const VEHICLE_NOT_ASSIGNED_MESSAGE = 'Vehicle not found - Selected Vehicle is not allocated to Driver';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  private activatedUrl = '';

  constructor(
    private readonly authInterceptorHelper: AuthInterceptorHelper,
    private readonly router: Router,
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.activatedUrl = event.url;
      });
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => this.handleErrors(error))
    );
  }

  private handleErrors(error: HttpErrorResponse): Observable<never> {
    const message: string = error?.error?.message || '';
    const status = error.status;
    const url = error.url || '';
    const statusText = error.statusText;
    if (status === 401) {
      const isAuthUrl = AUTH_SKIP_UNAUTHORIZED_URLS.some(authUrl => url.includes(authUrl));

      if (url.includes('api/v1/me')) {
        this.authInterceptorHelper.logout(true, false);
        this.redirectToErrorPage(ErrorPageType.SESSION_EXPIRED);
      }
      else if (!isAuthUrl) {
        this.authInterceptorHelper.logout();
        return throwError(() => error);
      }

    }

    if (status === 500) {
      const isAuthUrl = AUTH_ERROR_URLS.some(authUrl => url.includes(authUrl));
      this.redirectToErrorPage(isAuthUrl ? ErrorPageType.AUTH_ERROR : ErrorPageType.SERVER_ERROR);
      return throwError(() => error);
    }

    if (status === 0 && statusText === 'Unknown Error') {
      if (!navigator.onLine && !OFFLINE_MODE_SUPPORT_URLS.some(supportedUrl => this.activatedUrl.startsWith(supportedUrl))) {
        this.redirectToErrorPage(ErrorPageType.NETWORK_ERROR);
      }
      return throwError(() => error);
    }

    if (status === 400 && message.includes(VEHICLE_NOT_ASSIGNED_MESSAGE)) {
      this.router.navigateByUrl('/not-assign-to-vehicle');
    }

    return throwError(() => error);
  }

  private redirectToErrorPage(errorType: ErrorPageType): void {
    this.router.navigate(['/error'], {
      queryParams: { type: errorType }
    });
  }
}
